import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import DateTime24Input from '../components/DateTime24Input';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { FiClock, FiPlus, FiTrash2, FiBarChart, FiCalendar } from 'react-icons/fi';

const formatDuration = (start, end) => {
  const hours = (new Date(end) - new Date(start)) / (60 * 60 * 1000);
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const TimeTracking = () => {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    startDateTime: '',
    endDateTime: '',
    note: '',
  });
  const [dateFrom, setDateFrom] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  const [activeTab, setActiveTab] = useState('entries');

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.from = new Date(dateFrom).toISOString();
      if (dateTo) params.to = new Date(dateTo + 'T23:59:59.999Z').toISOString();
      const res = await axios.get('/time-entries', { params });
      setEntries(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load entries');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const params = {};
      if (dateFrom) params.from = new Date(dateFrom).toISOString();
      if (dateTo) params.to = new Date(dateTo + 'T23:59:59.999Z').toISOString();
      const res = await axios.get('/time-entries/stats', { params });
      setStats(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load stats');
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (activeTab === 'stats') fetchStats();
  }, [activeTab, dateFrom, dateTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.startDateTime || !formData.endDateTime) {
      setError('Start and end time are required');
      return;
    }
    const start = new Date(formData.startDateTime);
    const end = new Date(formData.endDateTime);
    if (end <= start) {
      setError('End time must be after start time');
      return;
    }
    try {
      await axios.post('/time-entries', {
        startDateTime: start.toISOString(),
        endDateTime: end.toISOString(),
        note: formData.note || '',
      });
      setFormData({ startDateTime: '', endDateTime: '', note: '' });
      fetchEntries();
      if (activeTab === 'stats') fetchStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save entry');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this time entry?')) return;
    try {
      await axios.delete(`/time-entries/${id}`);
      fetchEntries();
      if (activeTab === 'stats') fetchStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-background relative">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Time</h1>
            <p className="text-dark-100 text-sm">Work hours & stats</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          <label className="text-dark-100 text-sm self-center">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="bg-stroke border border-stroke rounded-lg px-3 py-2 text-white"
          />
          <label className="text-dark-100 text-sm self-center">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="bg-stroke border border-stroke rounded-lg px-3 py-2 text-white"
          />
        </div>

        <div className="flex gap-2 mb-6">
          <button
            type="button"
            onClick={() => setActiveTab('entries')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'entries' ? 'bg-purple-300 text-white' : 'bg-stroke text-dark-100 hover:bg-dark-300'
            }`}
          >
            Entries
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('stats')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'stats' ? 'bg-purple-300 text-white' : 'bg-stroke text-dark-100 hover:bg-dark-300'
            }`}
          >
            Statistics
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="bg-stroke border border-stroke rounded-xl p-6 mb-6 shadow-lg">
          <h2 className="text-lg font-semibold text-white mb-5 flex items-center gap-2">
            <FiPlus className="text-purple-300" /> Add entry
          </h2>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-white">Start</label>
              <DateTime24Input
                value={formData.startDateTime}
                onChange={(v) => setFormData((p) => ({ ...p, startDateTime: v }))}
                required
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-white">End</label>
              <DateTime24Input
                value={formData.endDateTime}
                onChange={(v) => setFormData((p) => ({ ...p, endDateTime: v }))}
                required
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-dark-100">Note (optional)</label>
              <input
                type="text"
                value={formData.note}
                onChange={(e) => setFormData((p) => ({ ...p, note: e.target.value }))}
                placeholder="e.g. Project X"
                className="w-full bg-background border border-dark-300 rounded-lg px-4 py-3 text-white placeholder-dark-200 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent"
              />
            </div>
            <div className="pt-2">
              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-200 to-purple-300 text-white px-4 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity shadow-md"
              >
                <FiClock /> Add
              </button>
            </div>
          </form>
        </div>

        {activeTab === 'entries' && (
          <div className="bg-stroke border border-stroke rounded-lg overflow-hidden">
            <h2 className="text-lg font-semibold text-white p-4 border-b border-stroke flex items-center gap-2">
              <FiCalendar className="text-white" /> Entries
            </h2>
            {loading ? (
              <div className="p-12 flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-300" />
              </div>
            ) : entries.length === 0 ? (
              <div className="p-12 text-center text-dark-100">No entries in this period.</div>
            ) : (
              <ul className="divide-y divide-stroke">
                {entries.map((entry) => (
                  <li key={entry._id} className="flex flex-wrap items-center justify-between gap-4 p-4 hover:bg-white/5">
                    <div className="flex-1 min-w-0">
                      <span className="text-white font-medium">
                        {format(new Date(entry.startDateTime), 'dd MMM yyyy, HH:mm')} – {format(new Date(entry.endDateTime), 'HH:mm')}
                      </span>
                      <span className="text-purple-200 ml-2">{formatDuration(entry.startDateTime, entry.endDateTime)}</span>
                      {entry.note && <p className="text-dark-100 text-sm mt-1">{entry.note}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDelete(entry._id)}
                      className="p-2 text-dark-100 hover:text-red-400 transition-colors"
                      title="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="bg-stroke border border-stroke rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
              <FiBarChart /> Statistics
            </h2>
            {statsLoading ? (
              <div className="p-12 flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-300" />
              </div>
            ) : stats ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-background border border-stroke rounded-lg p-6">
                    <span className="text-dark-100 text-sm block mb-1">Total hours</span>
                    <p className="text-3xl font-bold text-white">{stats.totalHours}</p>
                  </div>
                  <div className="bg-background border border-stroke rounded-lg p-6">
                    <span className="text-dark-100 text-sm block mb-1">Overtime hours</span>
                    <p className="text-3xl font-bold text-amber-400">{stats.totalOvertime}</p>
                  </div>
                  <div className="bg-background border border-stroke rounded-lg p-6">
                    <span className="text-dark-100 text-sm block mb-1">Standard day (hours)</span>
                    <p className="text-3xl font-bold text-purple-300">{stats.standardHoursPerDay}</p>
                  </div>
                </div>
                {stats.byDay && stats.byDay.length > 0 && (
                  <div>
                    <h3 className="text-white font-medium mb-3">By day</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="text-dark-100 text-sm border-b border-stroke">
                            <th className="pb-2 pr-4">Date</th>
                            <th className="pb-2 pr-4">Hours</th>
                            <th className="pb-2">Overtime</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.byDay.map((row) => (
                            <tr key={row.date} className="border-b border-stroke text-white">
                              <td className="py-2 pr-4">{row.date}</td>
                              <td className="py-2 pr-4">{row.hours}</td>
                              <td className="py-2 text-amber-400">{row.overtime}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-dark-100">No data for this period.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeTracking;
